/**
 * IndexedDB storage behind the map tile cache (`map-tile-cache.ts`).
 *
 * IndexedDB is per origin and every agency has its own subdomain, so a department only ever
 * sees its own copies. In the desktop app the origin survives frontend deploys and
 * cad-electron's HTTP-cache clearing, which is the point: the browser's own cache does not.
 *
 * Each copy is two records:
 *   - `entries` holds a small description of it (size, age, last use), indexed for eviction
 *     and the age sweep;
 *   - `bodies` holds the payload under the same key.
 * Eviction and the sweep walk `entries` only, so they never read megabytes of tiles to
 * decide what to delete.
 *
 * Every write also updates a running byte total in the same transaction. CAD's windows share
 * one database, so a total kept in memory would be wrong as soon as two windows wrote.
 */

export const MAP_TILE_CACHE_DB_NAME = 'm43-map-tile-cache';

/** Bump to change the schema. The upgrade drops everything and starts again: it is a cache. */
const DB_VERSION = 1;
const ENTRIES_STORE = 'entries';
const BODIES_STORE = 'bodies';
const META_STORE = 'meta';
const LAST_USED_INDEX = 'lastUsedAt';
const STORED_AT_INDEX = 'storedAt';
const TOTALS_KEY = 'totals';
const OPEN_TIMEOUT_MS = 2_000;
const DELETE_TIMEOUT_MS = 5_000;

/** Eviction overshoots the budget so the next few writes don't each trigger another one. */
export const EVICT_TO_FRACTION = 0.8;

export type CachedResponseType = 'json' | 'array-buffer' | 'image';

export type MapTileCacheEntry = {
    key: string;
    responseType: CachedResponseType;
    bytes: number;
    /** When this copy came from the network. Freshness is measured from here. */
    storedAt: number;
    /** When this copy was last served, for least-recently-used eviction. */
    lastUsedAt: number;
};

/** An entry plus its payload: an ArrayBuffer, a PNG Blob, or a JSON value. */
export type MapTileCacheRecord = MapTileCacheEntry & { body: unknown };

export type SweepResult = {
    entryCount: number;
    totalBytes: number;
    purged: number;
    evicted: number;
};

export type MapTileCacheStore = {
    get(key: string): Promise<MapTileCacheRecord | undefined>;
    /** Writes the record, then evicts least recently used copies if the total is over `maxBytes`. */
    put(record: MapTileCacheRecord, maxBytes: number): Promise<{ evicted: number }>;
    /** Marks a copy as just used. */
    touch(key: string, lastUsedAt: number): Promise<void>;
    /** Evicts least recently used copies until the total is at most `fraction` of what it is now. */
    shrink(fraction: number): Promise<{ evicted: number }>;
    /**
     * Deletes copies stored more than `maxAgeMs` ago, recomputes the running total from what
     * is left (which also repairs any drift in it), and evicts down to budget.
     */
    sweep(options: { now: number; maxAgeMs: number; maxBytes: number }): Promise<SweepResult>;
    close(): void;
};

export function isMapTileCacheEntry(value: unknown): value is MapTileCacheEntry {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const entry = value as Partial<MapTileCacheEntry>;
    return (
        typeof entry.key === 'string' &&
        (entry.responseType === 'json' ||
            entry.responseType === 'array-buffer' ||
            entry.responseType === 'image') &&
        typeof entry.bytes === 'number' &&
        typeof entry.storedAt === 'number' &&
        typeof entry.lastUsedAt === 'number'
    );
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
    return new Promise((resolve, reject) => {
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
        transaction.onabort = () =>
            reject(transaction.error ?? new DOMException('Transaction aborted', 'AbortError'));
    });
}

function readTotal(value: unknown): number {
    const total = (value as { totalBytes?: unknown } | undefined)?.totalBytes;
    return typeof total === 'number' && Number.isFinite(total) && total > 0 ? total : 0;
}

/**
 * Deletes least recently used copies, oldest first, until `total` is at most `targetBytes`.
 * Runs inside the caller's transaction, which must cover `entries` and `bodies`.
 */
function evictLeastRecentlyUsed(
    entries: IDBObjectStore,
    bodies: IDBObjectStore,
    total: number,
    targetBytes: number,
    keepKey?: string
): Promise<{ total: number; evicted: number }> {
    return new Promise((resolve, reject) => {
        let remaining = total;
        let evicted = 0;
        const request = entries.index(LAST_USED_INDEX).openCursor();
        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
            const cursor = request.result;
            if (!cursor || remaining <= targetBytes) {
                resolve({ total: Math.max(0, remaining), evicted });
                return;
            }
            const value: unknown = cursor.value;
            const key = isMapTileCacheEntry(value) ? value.key : undefined;
            // The copy just written has the newest `lastUsedAt`, so it is normally last in
            // line anyway; this makes sure a write can never evict itself.
            if (key !== keepKey) {
                cursor.delete();
                if (key !== undefined) {
                    bodies.delete(key);
                }
                remaining -= isMapTileCacheEntry(value) ? value.bytes : 0;
                evicted++;
            }
            cursor.continue();
        };
    });
}

function createStore(db: IDBDatabase): MapTileCacheStore {
    return {
        async get(key) {
            const transaction = db.transaction([ENTRIES_STORE, BODIES_STORE], 'readonly');
            const [entry, body] = await Promise.all([
                requestResult<unknown>(transaction.objectStore(ENTRIES_STORE).get(key)),
                requestResult<unknown>(transaction.objectStore(BODIES_STORE).get(key)),
            ]);
            if (!isMapTileCacheEntry(entry) || body === undefined) {
                return undefined;
            }
            return { ...entry, body };
        },

        async put(record, maxBytes) {
            const transaction = db.transaction(
                [ENTRIES_STORE, BODIES_STORE, META_STORE],
                'readwrite'
            );
            const done = transactionDone(transaction);
            const entries = transaction.objectStore(ENTRIES_STORE);
            const bodies = transaction.objectStore(BODIES_STORE);
            const meta = transaction.objectStore(META_STORE);

            const [previous, totals] = await Promise.all([
                requestResult<unknown>(entries.get(record.key)),
                requestResult<unknown>(meta.get(TOTALS_KEY)),
            ]);
            const { body, ...entry } = record;
            entries.put(entry);
            bodies.put(body, record.key);

            let total =
                readTotal(totals) +
                entry.bytes -
                (isMapTileCacheEntry(previous) ? previous.bytes : 0);
            let evicted = 0;
            if (total > maxBytes) {
                ({ total, evicted } = await evictLeastRecentlyUsed(
                    entries,
                    bodies,
                    total,
                    maxBytes * EVICT_TO_FRACTION,
                    record.key
                ));
            }
            meta.put({ totalBytes: Math.max(0, total) }, TOTALS_KEY);
            await done;
            return { evicted };
        },

        async touch(key, lastUsedAt) {
            const transaction = db.transaction(ENTRIES_STORE, 'readwrite');
            const done = transactionDone(transaction);
            const entries = transaction.objectStore(ENTRIES_STORE);
            const entry = await requestResult<unknown>(entries.get(key));
            if (isMapTileCacheEntry(entry)) {
                entries.put({ ...entry, lastUsedAt });
            }
            await done;
        },

        async shrink(fraction) {
            const transaction = db.transaction(
                [ENTRIES_STORE, BODIES_STORE, META_STORE],
                'readwrite'
            );
            const done = transactionDone(transaction);
            const meta = transaction.objectStore(META_STORE);
            const total = readTotal(await requestResult<unknown>(meta.get(TOTALS_KEY)));
            const result = await evictLeastRecentlyUsed(
                transaction.objectStore(ENTRIES_STORE),
                transaction.objectStore(BODIES_STORE),
                total,
                total * fraction
            );
            meta.put({ totalBytes: result.total }, TOTALS_KEY);
            await done;
            return { evicted: result.evicted };
        },

        async sweep({ now, maxAgeMs, maxBytes }) {
            const transaction = db.transaction(
                [ENTRIES_STORE, BODIES_STORE, META_STORE],
                'readwrite'
            );
            const done = transactionDone(transaction);
            const entries = transaction.objectStore(ENTRIES_STORE);
            const bodies = transaction.objectStore(BODIES_STORE);

            let totalBytes = 0;
            let entryCount = 0;
            let purged = 0;
            await new Promise<void>((resolve, reject) => {
                const request = entries.openCursor();
                request.onerror = () => reject(request.error);
                request.onsuccess = () => {
                    const cursor = request.result;
                    if (!cursor) {
                        resolve();
                        return;
                    }
                    const value: unknown = cursor.value;
                    if (!isMapTileCacheEntry(value) || now - value.storedAt > maxAgeMs) {
                        cursor.delete();
                        bodies.delete(cursor.primaryKey);
                        purged++;
                    } else {
                        totalBytes += value.bytes;
                        entryCount++;
                    }
                    cursor.continue();
                };
            });

            let evicted = 0;
            if (totalBytes > maxBytes) {
                const result = await evictLeastRecentlyUsed(
                    entries,
                    bodies,
                    totalBytes,
                    maxBytes * EVICT_TO_FRACTION
                );
                totalBytes = result.total;
                evicted = result.evicted;
                entryCount -= evicted;
            }
            transaction.objectStore(META_STORE).put({ totalBytes }, TOTALS_KEY);
            await done;
            return { entryCount, totalBytes, purged, evicted };
        },

        close() {
            db.close();
        },
    };
}

function openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        let settled = false;
        const timer = setTimeout(() => {
            settled = true;
            reject(new DOMException('Opening the map tile cache timed out', 'TimeoutError'));
        }, OPEN_TIMEOUT_MS);

        const request = indexedDB.open(MAP_TILE_CACHE_DB_NAME, DB_VERSION);
        request.onupgradeneeded = () => {
            const db = request.result;
            for (const name of Array.from(db.objectStoreNames)) {
                db.deleteObjectStore(name);
            }
            const entries = db.createObjectStore(ENTRIES_STORE, { keyPath: 'key' });
            entries.createIndex(LAST_USED_INDEX, 'lastUsedAt');
            entries.createIndex(STORED_AT_INDEX, 'storedAt');
            db.createObjectStore(BODIES_STORE);
            db.createObjectStore(META_STORE);
        };
        request.onsuccess = () => {
            const db = request.result;
            if (settled) {
                // Too late: the caller already gave up and moved on without a cache.
                db.close();
                return;
            }
            settled = true;
            clearTimeout(timer);
            // Another window deleting or upgrading the database waits on this connection.
            db.onversionchange = () => db.close();
            resolve(db);
        };
        request.onerror = () => {
            if (!settled) {
                settled = true;
                clearTimeout(timer);
                reject(request.error);
            }
        };
    });
}

export async function openMapTileCacheStore(): Promise<MapTileCacheStore> {
    if (typeof indexedDB === 'undefined') {
        throw new DOMException('IndexedDB is not available', 'NotSupportedError');
    }
    return createStore(await openDatabase());
}

/** Whether this origin has a map tile cache database at all. */
export async function mapTileCacheDatabaseExists(): Promise<boolean> {
    if (typeof indexedDB === 'undefined' || typeof indexedDB.databases !== 'function') {
        return false;
    }
    const databases = await indexedDB.databases();
    return databases.some((database) => database.name === MAP_TILE_CACHE_DB_NAME);
}

export function deleteMapTileCacheDatabase(): Promise<void> {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(
            () => reject(new DOMException('Deleting the map tile cache timed out', 'TimeoutError')),
            DELETE_TIMEOUT_MS
        );
        const request = indexedDB.deleteDatabase(MAP_TILE_CACHE_DB_NAME);
        request.onsuccess = () => {
            clearTimeout(timer);
            resolve();
        };
        request.onerror = () => {
            clearTimeout(timer);
            reject(request.error);
        };
    });
}
